import React, { useCallback, useMemo, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { observer } from "mobx-react";
import $translations from "@/core/translations/store";
import $user from "@/core/user/store";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import $locales from "@/core/locales/store";
import { translate } from "@/core/api/translate";

const Wrapper = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 25px;
  overflow-y: auto;
`;
const FieldsWrapper = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const InputWrapper = styled("div")`
  margin: 30px 0 10px;
`;

const Editor = observer(() => {
  const { code } = useParams();
  const active = $translations.getActiveMapped;
  const translatedKeys = useRef(new Set());

  useEffect(() => {
    $translations.setActive(code);
  }, [code]);

  useEffect(() => {
    if (!active || !active.translation) return;

    // Именно по коду, а не по позиции: список локалей строится из прав
    // пользователя и не обязан начинаться с ru.
    const master = active.translation.find((t) => t.code === "ru");
    if (!master || !master.value) return;

    const keyCode = active.code;
    if (translatedKeys.current.has(keyCode)) return;

    const empty = active.translation.filter((t) => t.code !== "ru" && !t.value);
    if (empty.length === 0) return;

    translatedKeys.current.add(keyCode);

    (async () => {
      for (const t of empty) {
        if ($translations.active !== keyCode) return;

        try {
          const { data } = await translate({
            text: master.value,
            from: "ru",
            to: t.code,
          });
          $translations.setTranslate(t.code, data.translations[0].text, keyCode);
        } catch (e) {
          console.warn(`Ошибка перевода для ${t.code}:`, e);
        }
      }
    })();
  }, [active]);

  const handleCopyCode = useCallback(() => {
    navigator.clipboard.writeText(active.code);
  }, [active?.code]);

  const handleTranslateChange = useCallback((code, value) => {
    $translations.setTranslate(code, value);
  }, []);

  const handleClear = useCallback(() => {
    if (active) {
      active.translation.forEach((t) => {
        if (t.code !== "ru") {
          $translations.setTranslate(t.code, "");
        }
      });
    }
  }, [active]);

  const memoizedTranslations = useMemo(() => {
    if (!active) return null;

    return active.translation.map((t, i) => (
      <InputWrapper key={`editor-${t.code}`}>
        <TextField
          multiline
          fullWidth
          rows={2}
          value={t.value}
          autoFocus={i === 0 && !t.value}
          label={$locales.getById(t.code)?.name || t.code}
          disabled={t.code === "ru" && $user.role !== "admin"}
          onChange={(e) => handleTranslateChange(t.code, e.target.value)}
        />
      </InputWrapper>
    ));
  }, [active, handleTranslateChange, $locales.getData.length]);

  const typographySx = useMemo(
    () => ({
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "action.hover",
      },
    }),
    []
  );

  const boxSx = useMemo(
    () => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 2,
    }),
    []
  );

  if (!active) return null;

  return (
    <Wrapper>
      <FieldsWrapper>
        <Box sx={boxSx}>
          <Typography variant="h5" onClick={handleCopyCode} sx={typographySx}>
            {active.code}
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            onClick={handleClear}
            size="small"
          >
            Очистить
          </Button>
        </Box>
        {memoizedTranslations}
      </FieldsWrapper>
    </Wrapper>
  );
});

export default Editor;
